import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { User, Lock, Save, Check } from "lucide-react";
import { displayRole } from "@/lib/roles";

const AVATAR_COLORS = [
    "#002FA7",
    "#10B981",
    "#EF4444",
    "#F59E0B",
    "#8B5CF6",
    "#06B6D4",
    "#F97316",
    "#EC4899",
    "#6B7280",
    "#14B8A6",
    "#0EA5E9",
    "#84CC16",
];

export default function SettingsPage() {
    const { user, setUser } = useAuth();
    const [profile, setProfile] = useState({
        name: user?.name || "",
        nickname: user?.nickname || "",
        job_title: user?.job_title || "",
        avatar: user?.avatar || "#002FA7",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setProfile({
            name: user?.name || "",
            nickname: user?.nickname || "",
            job_title: user?.job_title || "",
            avatar: user?.avatar || "#002FA7",
        });
    }, [user]);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await API.patch(`/users/${user.id}`, {
                name: profile.name,
                nickname: profile.nickname,
                job_title: profile.job_title,
                avatar: profile.avatar,
            });
            setUser(res.data);
            toast.success("Profil actualizat");
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const avatarColor = profile.avatar || "#002FA7";
    const displayName = profile.nickname || profile.name;

    return (
        <div className="p-6 max-w-2xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <h1 className="text-2xl font-bold text-[#111827] mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Setări</h1>

            {/* Profile */}
            <div className="bg-white border border-[#E4E4E7] rounded-md mb-6" data-testid="profile-settings">
                <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
                    <User size={16} className="text-[#9CA3AF]" />
                    <h2 className="font-semibold text-sm text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Profil</h2>
                </div>
                <form onSubmit={saveProfile} className="px-5 py-5 space-y-5">

                    {/* Avatar preview + color picker */}
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-3">Avatar</label>
                        <div className="flex items-center gap-5 mb-3">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                                style={{ backgroundColor: avatarColor }}
                                data-testid="avatar-preview"
                            >
                                <span className="text-white text-2xl font-bold select-none">
                                    {(displayName || user?.name || "U")[0]?.toUpperCase()}
                                </span>
                            </div>
                            <div>
                                <p className="font-semibold text-[#111827] text-sm">{displayName || user?.name}</p>
                                <p className="text-xs text-[#9CA3AF]">{profile.job_title || user?.email}</p>
                                <span className="text-xs bg-blue-50 text-[#002FA7] px-2 py-0.5 rounded-sm font-semibold mt-1 inline-block">{displayRole(user)}</span>
                            </div>
                        </div>
                        {/* Color swatches */}
                        <div className="flex flex-wrap gap-2" data-testid="avatar-color-picker">
                            {AVATAR_COLORS.map(color => (
                                <button
                                    key={color}
                                    type="button"
                                    data-testid={`avatar-color-${color.replace("#", "")}`}
                                    onClick={() => setProfile(p => ({ ...p, avatar: color }))}
                                    className="w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#002FA7]"
                                    style={{ backgroundColor: color }}
                                    title={color}
                                >
                                    {profile.avatar === color && (
                                        <Check size={14} className="text-white" strokeWidth={3} />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Name */}
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Nume complet *</label>
                        <input
                            data-testid="settings-name-input"
                            value={profile.name}
                            onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            required
                        />
                    </div>

                    {/* Nickname */}
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">
                            Poreclă <span className="text-[#9CA3AF] font-normal normal-case">(opțional)</span>
                        </label>
                        <input
                            data-testid="settings-nickname-input"
                            value={profile.nickname}
                            onChange={e => setProfile(p => ({ ...p, nickname: e.target.value }))}
                            placeholder="Cum te strigă colegii..."
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] placeholder:text-[#D1D5DB]"
                        />
                        <p className="text-xs text-[#9CA3AF] mt-1">Dacă este setată, porecla va fi afișată în loc de numele complet.</p>
                    </div>

                    {/* Job title */}
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">
                            Funcție <span className="text-[#9CA3AF] font-normal normal-case">(opțional)</span>
                        </label>
                        <input
                            data-testid="settings-job-title-input"
                            value={profile.job_title}
                            onChange={e => setProfile(p => ({ ...p, job_title: e.target.value }))}
                            placeholder="ex. Developer, Manager de Produs..."
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] placeholder:text-[#D1D5DB]"
                        />
                    </div>

                    {/* Email (read-only) */}
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Email</label>
                        <input
                            value={user?.email || ""}
                            disabled
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm bg-[#F8F9FA] text-[#9CA3AF]"
                        />
                    </div>

                    <button
                        data-testid="save-profile-btn"
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60 transition-colors"
                    >
                        <Save size={14} /> {saving ? "Se salvează..." : "Salvează profilul"}
                    </button>
                </form>
            </div>

            {/* Account Info */}
            <div className="bg-white border border-[#E4E4E7] rounded-md">
                <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
                    <Lock size={16} className="text-[#9CA3AF]" />
                    <h2 className="font-semibold text-sm text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Cont</h2>
                </div>
                <div className="px-5 py-5">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wide mb-1">Rol</p>
                            <p className="text-[#111827] font-medium">{displayRole(user)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wide mb-1">Status</p>
                            <p className="text-green-600 font-medium">{user?.status || "activ"}</p>
                        </div>
                        <div className="col-span-2">
                            <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wide mb-1">Creat la</p>
                            <p className="text-[#111827]">{user?.created_at ? new Date(user.created_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" }) : "-"}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
